import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import SCHSummerReport from "./SCHSummerReport";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/Card";
import { useToast } from "../hooks/useToast";
import { db } from "../lib/firebase";
import { collection, query, where, getDocs, getDoc, orderBy, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { formatCurrency, formatDate } from "../lib/utils";
import type { RouteOffer } from "../types/routeOffers";
import { MessageSquare } from "lucide-react";

interface Quote {
  id: string;
  quoteType: "hubstop" | "yearly" | "summer-camp" | "custom";
  status: "draft" | "pending" | "quoted" | "accepted" | "rejected" | "expired";
  guardianInfo: {
    firstName: string;
    lastName: string;
    email: string;
  };
  students: Array<{
    firstName: string;
    lastName: string;
    age?: number;
    carSeat?: string;
    boosterSeat?: boolean;
    additionalInfo?: string;
  }>;
  pricing: {
    totalPrice: number;
    perWeekPerStudent?: number;
    weekCount?: number;
  };
  hubstopInfo?: {
    name: string;
  };
  pickupAddress?: string;
  dropoffAddress?: string;
  selectedWeeks?: number[];
  createdAt: Date;
}

interface Registration {
  id: string;
  quoteType: string;
  guardianInfo: {
    firstName: string;
    lastName: string;
    email: string;
  };
  students: Array<{ firstName: string; lastName: string }>;
  pricing: {
    totalPrice: number;
  };
  emailVerified: boolean;
  status: string;
  createdAt: Date;
}

interface RouteInterest {
  id: string;
  routeId: string;
  routeName: string;
  userId: string;
  userEmail: string;
  zipcode: string;
  status: string;
  createdAt: Date;
  registrationCompleted?: boolean;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const { toast } = useToast();

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [routeInterests, setRouteInterests] = useState<RouteInterest[]>([]);
  const [offers, setOffers] = useState<RouteOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"quotes" | "registrations" | "routeInterests" | "offers" | "schoolReport">("quotes");
  const [referralStats, setReferralStats] = useState({
    code: "",
    credits: 0,
    totalReferrals: 0,
    referralLink: "",
  });
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [submittingFeedback, setSubmittingFeedback] = useState<Record<string, boolean>>({});

  // Check if coming from route interest
  useEffect(() => {
    if (location.state?.fromRouteInterest) {
      setActiveTab("routeInterests");
    }
  }, [location]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth?redirect=/dashboard");
    }
  }, [user, authLoading, navigate]);

  // Load user data
  useEffect(() => {
    if (!user) return;

    async function loadData() {
      try {
        console.log('[Dashboard] Starting to load data for user:', user.uid);

        // Helper to convert dates (handles both Timestamp and string formats)
        const toDate = (value: any) => {
          if (!value) return undefined;
          if (typeof value === 'string') return new Date(value);
          if (value.toDate) return value.toDate();
          return value;
        };

        // Load user profile for referral stats
        console.log('[Dashboard] Fetching user profile...');
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const referralCode = userData.referralCode || "";
          setReferralStats({
            code: referralCode,
            credits: userData.referralCredits || 0,
            totalReferrals: userData.totalReferrals || 0,
            referralLink: `${window.location.origin}/register?ref=${referralCode}`,
          });
          console.log('[Dashboard] User profile loaded');
        }

        // Load quotes
        console.log('[Dashboard] Fetching quotes...');
        const quotesQuery = query(
          collection(db, "quoteRequests"),
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const quotesSnapshot = await getDocs(quotesQuery);
        const loadedQuotes: Quote[] = quotesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        })) as Quote[];
        setQuotes(loadedQuotes);
        console.log('[Dashboard] Quotes loaded:', loadedQuotes.length);

        // Load registrations
        console.log('[Dashboard] Fetching registrations...');
        const registrationsQuery = query(
          collection(db, "shuttleRegistrations"),
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const registrationsSnapshot = await getDocs(registrationsQuery);
        const loadedRegistrations: Registration[] = registrationsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        })) as Registration[];
        setRegistrations(loadedRegistrations);
        console.log('[Dashboard] Registrations loaded:', loadedRegistrations.length);

        // Load route interests
        console.log('[Dashboard] Fetching route interests...');
        const routeInterestsQuery = query(
          collection(db, "routeInterests"),
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const routeInterestsSnapshot = await getDocs(routeInterestsQuery);
        const loadedRouteInterests: RouteInterest[] = routeInterestsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        })) as RouteInterest[];
        setRouteInterests(loadedRouteInterests);
        console.log('[Dashboard] Route interests loaded:', loadedRouteInterests.length);

        // Load route offers (customer view)
        console.log('[Dashboard] Fetching route offers...');

        let offersSnapshot;
        try {
          // Try query with orderBy (requires composite index)
          const offersQuery = query(
            collection(db, "routeOffers"),
            where("recipientUserIds", "array-contains", user.uid),
            orderBy("createdAt", "desc")
          );
          offersSnapshot = await getDocs(offersQuery);
        } catch (error: any) {
          // Fallback: If index not ready, query without orderBy and sort client-side
          console.log('[Dashboard] Index not ready, using fallback query');
          const fallbackQuery = query(
            collection(db, "routeOffers"),
            where("recipientUserIds", "array-contains", user.uid)
          );
          offersSnapshot = await getDocs(fallbackQuery);
        }

        const loadedOffers: RouteOffer[] = offersSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            expiresAt: toDate(data.expiresAt),
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            sentAt: toDate(data.sentAt),
            filledAt: toDate(data.filledAt),
            cancelledAt: toDate(data.cancelledAt),
            recipients: data.recipients?.map((r: any) => ({
              ...r,
              viewedAt: toDate(r.viewedAt),
              respondedAt: toDate(r.respondedAt),
              paidAt: toDate(r.paidAt),
              notifiedAt: toDate(r.notifiedAt),
            })) || [],
          } as RouteOffer;
        });

        // Sort client-side by createdAt (in case we used fallback query)
        loadedOffers.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });

        setOffers(loadedOffers);
        console.log('[Dashboard] Route offers loaded:', loadedOffers.length);

        console.log('[Dashboard] All data loaded successfully!');
      } catch (error) {
        console.error("[Dashboard] Error loading data:", error);
        toast({
          type: "error",
          title: "Failed to load data",
          description: "Please refresh the page.",
        });
      } finally {
        console.log('[Dashboard] Setting loading to false');
        setLoading(false);
      }
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Removed toast to prevent infinite loop


  // Get status badge color
  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: "bg-gray-100 text-gray-700",
      pending: "bg-yellow-100 text-yellow-700",
      quoted: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      expired: "bg-gray-100 text-gray-500",
      active: "bg-green-100 text-green-700",
      completed: "bg-gray-100 text-gray-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  // Get quote type label
  const getQuoteTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      hubstop: "Hubstop Route",
      yearly: "School Year",
      "summer-camp": "Summerside Camps at SCH",
      custom: "Custom Route",
    };
    return labels[type] || type;
  };

  // Handle feedback submission
  const handleSubmitFeedback = async (offerId: string) => {
    if (!feedback[offerId]?.trim() || !user) return;

    try {
      setSubmittingFeedback(prev => ({ ...prev, [offerId]: true }));

      const offer = offers.find(o => o.id === offerId);
      if (!offer) return;

      // Update the recipient's feedback in Firestore
      const offerRef = doc(db, 'routeOffers', offerId);
      const updatedRecipients = offer.recipients.map(r => {
        if (r.userId === user.uid) {
          return { ...r, enrollmentFeedback: feedback[offerId].trim() };
        }
        return r;
      });

      await updateDoc(offerRef, {
        recipients: updatedRecipients,
        updatedAt: serverTimestamp(),
      });

      // Update local state
      setOffers(prev => prev.map(o => {
        if (o.id === offerId) {
          return {
            ...o,
            recipients: updatedRecipients
          };
        }
        return o;
      }));

      // Clear feedback input
      setFeedback(prev => ({ ...prev, [offerId]: '' }));

      toast({
        type: 'success',
        title: 'Feedback Submitted',
        description: "Thank you! We'll be in touch soon.",
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        type: 'error',
        title: 'Error',
        description: 'Failed to submit feedback. Please try again or contact us directly.',
      });
    } finally {
      setSubmittingFeedback(prev => ({ ...prev, [offerId]: false }));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shuttlebee-yellow"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-1">Welcome back, {user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate("/")}>
                Home
              </Button>
              <Button variant="ghost" onClick={signOut}>
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/summer-camp-quote")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏕️</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Summerside Camps at SCH</h3>
                  <p className="text-sm text-gray-500">Summer Day Camp</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/custom-quote")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🗺️</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Custom Route</h3>
                  <p className="text-sm text-gray-500">Request a quote</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/routes")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔍</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Find Routes</h3>
                  <p className="text-sm text-gray-500">Route discovery</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/contact")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📧</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Contact Us</h3>
                  <p className="text-sm text-gray-500">Get in touch</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referral Program */}
        {referralStats.code && (
          <Card className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-900">
                <span className="text-2xl">🎁</span>
                Refer Friends & Earn $25 Credit
              </CardTitle>
              <CardDescription>
                Share your referral link and earn $25 for each family that signs up!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{referralStats.totalReferrals}</div>
                  <div className="text-sm text-gray-600">Total Referrals</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-2xl font-bold text-green-600">${referralStats.credits.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">Credits Earned</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-2xl font-bold text-green-600">${(referralStats.totalReferrals * 25).toFixed(2)}</div>
                  <div className="text-sm text-gray-600">Potential Earnings</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Referral Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralStats.referralLink}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                  />
                  <Button
                    variant="shuttlebee"
                    onClick={() => {
                      navigator.clipboard.writeText(referralStats.referralLink);
                      toast({
                        type: "success",
                        title: "Copied!",
                        description: "Referral link copied to clipboard",
                      });
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  <em><a href="/referral-terms" target="_blank" rel="noopener noreferrer" className="text-shuttlebee-dark hover:underline font-medium">Restrictions apply</a>. Credits will be applied once referred family completes registration.</em>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          <button
            onClick={() => setActiveTab("quotes")}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === "quotes"
                ? "text-shuttlebee-dark border-b-2 border-shuttlebee-yellow"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Quotes ({quotes.length})
          </button>
          <button
            onClick={() => setActiveTab("registrations")}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === "registrations"
                ? "text-shuttlebee-dark border-b-2 border-shuttlebee-yellow"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Registrations ({registrations.length})
          </button>
          <button
            onClick={() => setActiveTab("routeInterests")}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === "routeInterests"
                ? "text-shuttlebee-dark border-b-2 border-shuttlebee-yellow"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Route Interests ({routeInterests.length})
          </button>
          <button
            onClick={() => setActiveTab("offers")}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === "offers"
                ? "text-shuttlebee-dark border-b-2 border-shuttlebee-yellow"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Offers ({offers.length})
          </button>
          {role === 'admin' && (
            <button
              onClick={() => setActiveTab("schoolReport")}
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === "schoolReport"
                  ? "text-shuttlebee-dark border-b-2 border-shuttlebee-yellow"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              School Report
            </button>
          )}
        </div>

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <div className="space-y-4">
            {quotes.length > 0 ? (
              quotes.map((quote) => (
                <Card key={quote.id}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {getQuoteTypeLabel(quote.quoteType)}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(quote.status)}`}>
                            {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          {quote.students && quote.students.length > 0 && (
                            <>
                              {quote.students.some(s => s.firstName || s.lastName) && (
                                <>
                                  <p>
                                    <strong>Students ({quote.students.length}):</strong>{" "}
                                    {quote.students.filter(s => s.firstName && s.lastName).map((s) => `${s.firstName} ${s.lastName}`).join(", ")}
                                  </p>
                                  {quote.students.some(s => s.age || s.carSeat || s.boosterSeat || s.additionalInfo) && (
                                    <div className="mt-2 pl-4 border-l-2 border-gray-200">
                                      {quote.students.filter(s => s.firstName && s.lastName).map((student, idx) => (
                                        <div key={idx} className="mb-2">
                                          <p className="font-medium text-gray-700">{student.firstName} {student.lastName}</p>
                                          {student.age && <p className="text-xs">Age: {student.age}</p>}
                                          {student.carSeat && <p className="text-xs">Car Seat: {student.carSeat}</p>}
                                          {student.boosterSeat && <p className="text-xs">Booster Seat Required</p>}
                                          {student.additionalInfo && <p className="text-xs">Notes: {student.additionalInfo}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}
                              {!quote.students.some(s => s.firstName || s.lastName) && (
                                <p>
                                  <strong>Students:</strong> {quote.students.length} student{quote.students.length !== 1 ? 's' : ''}
                                </p>
                              )}
                            </>
                          )}
                          {quote.hubstopInfo && (
                            <p>
                              <strong>Hubstop:</strong> {quote.hubstopInfo.name}
                            </p>
                          )}
                          {quote.pickupAddress && (
                            <p>
                              <strong>Pickup:</strong> {quote.pickupAddress}
                            </p>
                          )}
                          {quote.selectedWeeks && (
                            <p>
                              <strong>Weeks:</strong> {quote.selectedWeeks.length} weeks selected
                            </p>
                          )}
                          <p className="text-gray-400">
                            Created {formatDate(quote.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-shuttlebee-dark">
                            {formatCurrency(quote.pricing.totalPrice)}
                          </p>
                          {quote.pricing.perWeekPerStudent && (
                            <p className="text-sm text-gray-500">
                              {formatCurrency(quote.pricing.perWeekPerStudent)}/week per camper, roundtrip
                            </p>
                          )}
                        </div>
                        {quote.status === "accepted" && (
                          <span className="text-green-600 text-sm font-medium">
                            ✓ Quote Accepted
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <div className="text-5xl mb-4">📋</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No quotes yet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Get started by requesting a quote for your transportation needs.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button onClick={() => navigate("/hubstop-quote")}>
                      Hubstop Quote
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/custom-quote")}>
                      Custom Route
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Registrations Tab */}
        {activeTab === "registrations" && (
          <div className="space-y-4">
            {registrations.length > 0 ? (
              registrations.map((registration) => (
                <Card key={registration.id}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {getQuoteTypeLabel(registration.quoteType)}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(registration.status)}`}>
                            {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                          </span>
                          {!registration.emailVerified && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              Email Not Verified
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>
                            <strong>Confirmation #:</strong>{" "}
                            {registration.id.substring(0, 8).toUpperCase()}
                          </p>
                          <p>
                            <strong>Students:</strong>{" "}
                            {registration.students.map((s) => `${s.firstName} ${s.lastName}`).join(", ")}
                          </p>
                          <p className="text-gray-400">
                            Registered {formatDate(registration.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-shuttlebee-dark">
                            {formatCurrency(registration.pricing.totalPrice)}
                          </p>
                        </div>
                        <Button variant="outline" size="sm">
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <div className="text-5xl mb-4">🚐</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No registrations yet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Accept a quote to create your first registration.
                  </p>
                  <Button onClick={() => setActiveTab("quotes")}>
                    View Your Quotes
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Route Interests Tab */}
        {activeTab === "routeInterests" && (
          <div className="space-y-4">
            {routeInterests.length > 0 ? (
              routeInterests.map((interest) => (
                <Card key={interest.id}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            Route Interest
                          </span>
                          <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                            interest.registrationCompleted
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}>
                            {interest.registrationCompleted ? "Completed" : "Pending Registration"}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {interest.routeName}
                        </h3>
                        {interest.zipcode && interest.zipcode !== "not specified" && (
                          <p className="text-sm text-gray-600">
                            Zipcode: {interest.zipcode}
                          </p>
                        )}
                        <p className="text-gray-400 text-sm mt-2">
                          Expressed interest {formatDate(interest.createdAt)}
                        </p>

                        {!interest.registrationCompleted && (
                          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">⏰</span>
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-1">Complete Your Registration</h4>
                                <p className="text-sm text-gray-700 mb-3">
                                  You've shown interest in this route! Please complete your full registration
                                  to secure your spot and finalize your transportation details.
                                </p>
                                <Button
                                  size="sm"
                                  onClick={() => navigate("/summer-camp-quote")}
                                  className="bg-yellow-600 hover:bg-yellow-700"
                                >
                                  Complete Registration Now
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <div className="text-5xl mb-4">🗺️</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No route interests yet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Browse available routes and join one that works for you.
                  </p>
                  <Button onClick={() => navigate("/routes")}>
                    Browse Routes
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === "offers" && (
          <div className="space-y-4">
            {offers.length > 0 ? (
              offers.map((offer) => {
                // Find current user's recipient entry
                const myRecipient = offer.recipients.find(r => r.userId === user?.uid);
                const isExpired = offer.expiresAt && new Date(offer.expiresAt) < new Date();

                return (
                  <Card key={offer.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg text-gray-900">
                              {offer.routeName}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              myRecipient?.status === 'accepted' ? 'bg-green-100 text-green-700' :
                              myRecipient?.status === 'declined' ? 'bg-red-100 text-red-700' :
                              myRecipient?.status === 'viewed' ? 'bg-blue-100 text-blue-700' :
                              isExpired ? 'bg-gray-100 text-gray-500' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {myRecipient?.status === 'accepted' ? 'Accepted' :
                               myRecipient?.status === 'declined' ? 'Declined' :
                               myRecipient?.status === 'viewed' ? 'Viewed' :
                               isExpired ? 'Expired' : 'Pending'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>
                              <strong>Route:</strong> {offer.pickupLocation} → {offer.dropoffLocation}
                            </p>
                            <p>
                              <strong>Available Weeks:</strong> {offer.availableWeeks.length} weeks
                            </p>
                            <p>
                              <strong>Rate:</strong> {formatCurrency(offer.adjustedWeeklyRate)}/week per student
                            </p>
                            <p>
                              <strong>Expires:</strong> {formatDate(offer.expiresAt)}
                              {isExpired && <span className="text-red-600 ml-2">(Expired)</span>}
                            </p>
                            <p className="text-gray-400">
                              Received {formatDate(offer.createdAt)}
                            </p>
                          </div>

                          {/* Feedback Section for Paid Offers */}
                          {myRecipient?.status === 'paid' && (
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start gap-3 mb-3">
                                <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <h4 className="font-semibold text-blue-900 mb-1">Route Update</h4>
                                  <p className="text-sm text-blue-800">
                                    Great news — this route has reached the minimum enrollment needed to move forward!
                                  </p>
                                  <p className="text-sm text-blue-800 mt-2">
                                    We may still add a stop to help fill the vehicle or accommodate additional families.
                                    If you have timing preferences or conflicts that could affect your participation,
                                    please let us know below. We'll follow up within two weeks if we're unable to accommodate specific requests.
                                  </p>
                                </div>
                              </div>

                              {myRecipient.enrollmentFeedback ? (
                                <div className="bg-white border border-blue-200 rounded-lg p-3">
                                  <p className="text-sm font-medium text-blue-900 mb-1">Your Feedback:</p>
                                  <p className="text-sm text-gray-700">{myRecipient.enrollmentFeedback}</p>
                                </div>
                              ) : (
                                <>
                                  <textarea
                                    value={feedback[offer.id] || ''}
                                    onChange={(e) => setFeedback(prev => ({ ...prev, [offer.id]: e.target.value }))}
                                    placeholder="Share any timing preferences, conflicts, or other concerns..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
                                  />
                                  <Button
                                    onClick={() => handleSubmitFeedback(offer.id)}
                                    disabled={submittingFeedback[offer.id] || !feedback[offer.id]?.trim()}
                                    size="sm"
                                    className="mt-2 w-full bg-blue-600 hover:bg-blue-700"
                                  >
                                    {submittingFeedback[offer.id] ? 'Submitting...' : 'Submit Feedback'}
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-shuttlebee-dark">
                              {formatCurrency(offer.adjustedWeeklyRate)}
                            </p>
                            <p className="text-sm text-gray-500">per week per student</p>
                          </div>
                          <Button
                            onClick={() => navigate(`/offers/${offer.id}`)}
                            variant={myRecipient?.status === 'pending' ? 'default' : 'outline'}
                            size="sm"
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <div className="text-5xl mb-4">📬</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No route offers yet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    You'll see route offers here when an administrator sends one to you.
                  </p>
                  <Button onClick={() => navigate("/routes")}>
                    Browse Available Routes
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* School Report Tab (admin only) */}
        {activeTab === "schoolReport" && role === 'admin' && (
          <div className="-mx-4 sm:-mx-6 lg:-mx-8">
            <SCHSummerReport />
          </div>
        )}
      </div>
    </div>
  );
}
